import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, useLocation } from 'react-router-dom';
import CookieConsent from 'react-cookie-consent';
import './App.css';
import PostDetails from './PostDetails';
import './i18n';
import About from './About';
import PrivacyPolicy from './PrivacyPolicy';
import ResponsiveAppBar from './ResponsiveAppBar';
import AdComponent from './AdComponent';
import PrayerChatWidget from './PrayerChatWidget';
import PushNotificationBanner from './PushNotificationBanner';
import DevotionalSubscribeWidget from './DevotionalSubscribeWidget';
import Footer from './Footer';
import YouTubeSection from './YouTubeSection';
import { structurePosts, themeTint, shorten } from './postFormatter';

const POSTS_PER_PAGE = 9;

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function Home({ posts }) {
  const [query, setQuery] = useState('');
  const [visible, setVisible] = useState(POSTS_PER_PAGE);

  // Âncora "Baixar o app" (/#app): o conteúdo carrega async, então o scroll
  // nativo do browser não encontra a seção — posiciona quando os posts chegam
  useEffect(() => {
    if (window.location.hash === '#app' && posts.length) {
      const el = document.getElementById('app');
      if (el) {
        window.scrollTo(0, el.getBoundingClientRect().top + window.pageYOffset - 80);
      }
    }
  }, [posts.length]);

  const hero = posts[0];
  const q = query.toLowerCase();
  const filtered = q ? posts.filter((post) => post.title.toLowerCase().includes(q)) : posts;
  const shown = filtered.slice(0, visible);
  const todayDate = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long' });

  const handleSearch = (e) => {
    setQuery(e.target.value);
    setVisible(POSTS_PER_PAGE);
  };

  return (
    <main className="fe-main">
      {hero && (
        <section className="fe-hero">
          <div className="fe-hero-inner">
            <div>
              <div className="fe-hero-kicker">Mensagem de hoje · {todayDate}</div>
              <h1 className="fe-hero-title">{hero.title}</h1>
              <p className="fe-hero-excerpt">{hero.excerpt}</p>
              <Link to={`/posts/${hero.id}`} className="fe-hero-cta">Ler a reflexão completa</Link>
            </div>
            {hero.verse ? (
              <div className="fe-verse-card">
                <div className="fe-label">Versículo do dia</div>
                <div className="fe-verse-card-text">“{hero.verse.text}”</div>
                <div className="fe-verse-ref">{hero.verse.reference}</div>
              </div>
            ) : (
              <div className="fe-verse-card fe-hero-mock">
                <div className="fe-label">Oração do dia</div>
                <div className="fe-hero-mock-prayer">{hero.shortPrayer}</div>
                <a href="#app" className="fe-hero-mock-cta">Baixar o app</a>
              </div>
            )}
          </div>
        </section>
      )}

      <div className="fe-ad-slot">
        <AdComponent />
      </div>

      <section className="fe-section-head">
        <h2>Reflexões recentes</h2>
        <input
          type="text"
          className="fe-search"
          value={query}
          onChange={handleSearch}
          placeholder="Buscar reflexão..."
        />
      </section>

      <section className="fe-grid">
        {shown.map((post) => (
          <Link to={`/posts/${post.id}`} key={post.id} className="fe-card">
            <div className="fe-card-strip" style={{ background: themeTint(post.theme) }}>
              <div className="fe-card-theme">{post.theme}</div>
              {post.verse ? (
                <>
                  <div className="fe-card-verse">“{shorten(post.verse.text, 80)}”</div>
                  <div className="fe-card-verse-ref">{post.verse.reference}</div>
                </>
              ) : (
                <div className="fe-card-noverse">{shorten(post.reflection, 80)}</div>
              )}
            </div>
            <div className="fe-card-body">
              <h3>{post.title}</h3>
              <p>{post.excerpt}</p>
              <span className="fe-card-more">Ler mais →</span>
            </div>
          </Link>
        ))}
      </section>

      {filtered.length > visible && (
        <div className="fe-load-more-wrap">
          <button
            type="button"
            className="fe-load-more"
            onClick={() => setVisible((v) => v + POSTS_PER_PAGE)}
          >
            Carregar mais reflexões
          </button>
        </div>
      )}

      <YouTubeSection />

      <section className="fe-app-promo-wrap" id="app">
        <div className="fe-app-promo">
          <div className="fe-app-promo-text">
            <h2>Leve o Fé Escrita com você</h2>
            <p>
              A mensagem do dia, diário espiritual e pedidos de oração no aplicativo — o mesmo
              conteúdo do site, feito para o seu dia a dia.
            </p>
            {/* Trocar pelos badges oficiais das lojas quando os links existirem */}
            <div className="fe-badges">
              <a href="#app" onClick={(e) => e.preventDefault()}>App Store</a>
              <a href="#app" onClick={(e) => e.preventDefault()}>Google Play</a>
            </div>
          </div>
          {hero && (
            <div className="fe-app-mock">
              <div className="fe-label">Mensagem de hoje</div>
              <div className="fe-app-mock-title">{hero.title}</div>
              {hero.verse ? (
                <div className="fe-app-mock-verse">“{shorten(hero.verse.text, 80)}”</div>
              ) : (
                <div className="fe-app-mock-verse fe-no-italic">{shorten(hero.reflection, 80)}</div>
              )}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

function App() {
  const [posts, setPosts] = useState([]);

  // Carregar posts a partir da API do backend
  useEffect(() => {
    fetch('https://feescrita-138417386089.us-central1.run.app/api/posts')
      .then((response) => {
        if (!response.ok) {
          throw new Error('Erro ao buscar posts do backend');
        }
        return response.json();
      })
      .then((data) => {
        const formattedPosts = structurePosts(
          data.map((post) => ({
            ...post,
            id: Number(post.id), // Garante que o ID seja um número
          }))
        );
        setPosts(formattedPosts);
      })
      .catch((error) => {
        console.error('Erro ao carregar os posts:', error);
      });
  }, []);

  const handleAcceptCookies = () => {
    console.log('Usuário aceitou os cookies');
    const gtagScript = document.createElement('script');
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=G-4B431TTMT2`;
    gtagScript.async = true;
    document.body.appendChild(gtagScript);

    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    gtag('js', new Date());
    gtag('config', 'G-4B431TTMT2');

    const adsenseScript = document.createElement('script');
    adsenseScript.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';
    adsenseScript.async = true;
    adsenseScript.setAttribute('data-ad-client', 'ca-pub-3458489837498636');
    document.body.appendChild(adsenseScript);
  };

  return (
    <Router>
      <div className="App">
        <ScrollToTop />
        <ResponsiveAppBar />

        <Routes>
          <Route exact path="/" element={<Home posts={posts} />} />
          <Route path="/posts/:id" element={<PostDetails />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/privacidade" element={<PrivacyPolicy />} />
        </Routes>

        <Footer />

        <CookieConsent
          location="bottom"
          buttonText="Aceitar Cookies"
          onAccept={handleAcceptCookies}
          style={{ background: '#2B373B' }}
          buttonStyle={{ color: '#4e503b', fontSize: '13px' }}
        >
          Este site utiliza cookies para melhorar sua experiência.
        </CookieConsent>

        <PushNotificationBanner />
        <PrayerChatWidget />
        <DevotionalSubscribeWidget />
      </div>
    </Router>
  );
}

export default App;
